import { Injectable } from '@angular/core';
import { Observable ,  of, forkJoin } from 'rxjs';
import urlJoin from 'url-join';
import { HttpClient, HttpParams } from '@angular/common/http';
import { PaymentDisplayAggregateInfo } from './payment-display-aggregate-info';
import { PaymentFileInfo } from './payment-file-info.model';
import { PaymentsPaidByPayDateInfo } from './payments-paid-date-info.model';
import { SettingsService } from '../shared/services/settings/settings.service';
import { Moment } from 'moment';
import { flatMap, map } from 'rxjs/operators';
import { ROUTE_TOKEN_GATEWAY_URL } from '../shared/interceptors/gateway-url.interceptor';

@Injectable()
export class PaymentsService {

  constructor(
    private http: HttpClient,
    private settings: SettingsService) {
  }

  getPaymentInfo(payGroups: string, payDate: Moment,
    sequenceNumbers: number[]): Observable<PaymentDisplayAggregateInfo> {

    const path = `${encodeURI(this.settings.tenantId)}/`;
    const infoPaymentsPaid = this.http.get<PaymentsPaidByPayDateInfo[]>(
      this._getUriGetPaymentsPaidByPayDayInfos(path,
        this._getQueryParametersPayDayInfos(payGroups, payDate, sequenceNumbers)));

    return infoPaymentsPaid.pipe(
      flatMap((result: PaymentsPaidByPayDateInfo[]) => {
        const hasPayments = result.length > 0;
        const possibleOutstandingPaymentFiles = !result.every((elem) => elem.possibleOutstandingPaymentFiles === false);
        const paymentIds = result.map((elem) => elem.paidPaymentIds).reduce((a, b) => a.concat(b), []).join(',');
        if (paymentIds.length > 0) {
          return forkJoin([
              of(hasPayments),
              of(possibleOutstandingPaymentFiles),
              this.http.get<PaymentFileInfo[]>(
                this._getUriCountSentNachaFiles(`/${paymentIds}/tenants/${encodeURI(this.settings.tenantId)}`)
              )
            ]
          );
        } else {
          return forkJoin([
              of(hasPayments),
              of(possibleOutstandingPaymentFiles),
              of([])
            ]
          );
        }
      }),
      map((finalResult: [boolean, boolean, PaymentFileInfo[]]) =>
        new PaymentDisplayAggregateInfo(finalResult[0], finalResult[1], finalResult[2]))
    );
  }

  // URL for caling GetPaymentsPaidByPayDayInfos() endpoint
  private _getUriGetPaymentsPaidByPayDayInfos(path: string, params: string) {
    let uri = urlJoin(this._getBaseUrl(), `payment/v1/tenants/`);
    uri = urlJoin(uri, path);
    if (params) {
      uri += 'paymentsPaidByPayDayInfos?' + params;
    }
    return uri;
  }

  private _getBaseUrl(): string {
    return ROUTE_TOKEN_GATEWAY_URL;
  }

  // Params for GetPaymentsPaidByPayDayInfos()
  private _getQueryParametersPayDayInfos(payGroups: string, payDate: Moment, sequenceNumbers: number []): string {
    return new HttpParams()
      .append('payGroups', payGroups)
      .append('fromPayDate', payDate.startOf('day').toISOString())
      .append('toPayDate', payDate.endOf('day').toISOString())
      .append('sequenceNumbers', (sequenceNumbers || []).join(','))
      .toString();
  }

  // URL for calling CountSentNachaFiles() endpoint
  // "params" is equal to paymentIds for this API call
  private _getUriCountSentNachaFiles(path: string) {
    let uri = urlJoin(this._getBaseUrl(), `banking/v1/payments`);
    uri = urlJoin(uri, path) + '/sentnachafileinfos';
    return uri;
  }

  // Params for CountSentNachaFiles()
  private _getQueryParametersFileInfos(paymentIds: string): string {
    return new HttpParams().append('paymentIds', paymentIds).toString();
  }
}
